/**
 * ApiSpecDiffView — structured side-by-side diff for API endpoint specs.
 *
 * Layout per side:
 *   1. Route header — METHOD badge + path
 *   2. Auth type
 *   3. Summary / Description
 *   4. Parameters table
 *   5. Request Body — collapsible schema tree
 *   6. Responses — per status code, collapsible schema trees
 *
 * Sections that are identical between before/after are collapsed by default.
 * Changed sections are open with diff highlighting.
 */
import { useState, useMemo, useRef, useCallback, useEffect, createContext, useContext } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { schemaType, schemaTypeName, schemaTypeLabel } from "../../utils/schemaType";

/* ════════════════════════════════════════════════════════════════════
   Shared expand context for synced collapse/expand across panels
   ════════════════════════════════════════════════════════════════════ */

type ExpandCtx = {
  expandMap: Record<string, boolean>;
  toggle: (key: string) => void;
};
const ExpandContext = createContext<ExpandCtx | null>(null);
function useExpand() { return useContext(ExpandContext); }

/* ════════════════════════════════════════════════════════════════════
   Types
   ════════════════════════════════════════════════════════════════════ */

interface ApiParam {
  name: string; in: string; required?: boolean; description?: string;
  example?: unknown; schema?: { type?: string | string[]; enum?: string[]; format?: string };
}
interface ResponseObj { description?: string; schema?: SchemaObj | null }
interface SchemaObj {
  // May be a union array (e.g. ["number", "null"]) — read via utils/schemaType.
  type?: string | string[]; properties?: Record<string, SchemaObj>; items?: SchemaObj;
  required?: string[]; description?: string; enum?: string[]; format?: string;
  example?: unknown; allOf?: SchemaObj[]; nullable?: boolean;
  [k: string]: unknown;
}

export interface ApiSpecDiffViewProps {
  beforeSpec: Record<string, any> | null;
  afterSpec: Record<string, any> | null;
  beforeLabel?: string;
  afterLabel?: string;
}

/* ════════════════════════════════════════════════════════════════════
   Helpers
   ════════════════════════════════════════════════════════════════════ */

const METHOD_COLORS: Record<string, string> = {
  GET: "bg-emerald-100 text-emerald-700", POST: "bg-blue-100 text-blue-700",
  PUT: "bg-amber-100 text-amber-700", PATCH: "bg-orange-100 text-orange-700",
  DELETE: "bg-red-100 text-red-700",
};
const AUTH_LABELS: Record<string, string> = {
  none: "No Auth", partner_token: "Partner Token",
  client_token: "Client Token", user_token: "User Token",
};

const AUTH_BADGE_COLORS: Record<string, string> = {
  none: "bg-slate-100 text-slate-500",
  partner_token: "bg-purple-100 text-purple-700",
  client_token: "bg-sky-100 text-sky-700",
  user_token: "bg-teal-100 text-teal-700",
};

const TYPE_CHIP_STYLES: Record<string, string> = {
  string: "bg-blue-50 text-blue-600 border-blue-200",
  integer: "bg-amber-50 text-amber-600 border-amber-200",
  number: "bg-amber-50 text-amber-600 border-amber-200",
  boolean: "bg-green-50 text-green-600 border-green-200",
  object: "bg-purple-50 text-purple-600 border-purple-200",
  array: "bg-orange-50 text-orange-600 border-orange-200",
  enum: "bg-pink-50 text-pink-600 border-pink-200",
};

function TypeChip({ type }: { type: string }) {
  // Labels can be array types ("string[]") or unions ("number | null") — colour
  // off the leading member. String() guards a non-string from a raw spec.
  const label = String(type);
  const head = label.split(" | ")[0];
  const baseType = head.endsWith("[]") ? head.slice(0, -2) : head;
  const style = TYPE_CHIP_STYLES[baseType] ?? TYPE_CHIP_STYLES[label] ?? "bg-slate-50 text-slate-500 border-slate-200";
  return (
    <span className={`inline-block text-[11px] font-mono font-medium px-1.5 py-0.5 rounded border ${style}`}>
      {label}
    </span>
  );
}

function eq(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/* ─── Side-aware diff helpers ──────────────────────────────────────────
 * The spec viewer renders the same component on BOTH sides of a side-by-
 * side diff. Each render needs to know which side it is so it can apply
 * the correct semantics:
 *
 *   - On the BEFORE side, an item that exists here but not on the other
 *     side is REMOVED in the new version → red badge.
 *   - On the AFTER side, the same kind of item is ADDED → green badge.
 *   - On both sides, an item present in BOTH but with a different value
 *     is MODIFIED → blue badge.
 *   - To keep rows aligned across the two columns, an item that exists
 *     ONLY on the OTHER side gets rendered as a dim placeholder row on
 *     this side.
 *
 * Helper: classifyItem returns the per-row status given the item's
 * presence on each side.
 */
type DiffSide = "before" | "after";
type ItemStatus = "unchanged" | "added" | "removed" | "modified" | "placeholder";

function classifyItem(
  side: DiffSide,
  inThis: boolean,
  inOther: boolean,
  isEqual: boolean,
): ItemStatus {
  if (inThis && inOther) return isEqual ? "unchanged" : "modified";
  if (inThis && !inOther) return side === "after" ? "added" : "removed";
  if (!inThis && inOther) return "placeholder"; // alignment row
  return "unchanged";
}

const STATUS_BG: Record<ItemStatus, string> = {
  unchanged: "bg-slate-50",
  added: "bg-emerald-50/60",
  removed: "bg-red-50/60",
  modified: "bg-blue-50/40",
  placeholder: "bg-slate-50/30",
};
const STATUS_ROW_BG: Record<ItemStatus, string> = {
  unchanged: "",
  added: "bg-emerald-50/40",
  removed: "bg-red-50/40",
  modified: "bg-blue-50/30",
  placeholder: "bg-slate-50/30",
};

function flattenSchema(s: SchemaObj | null | undefined): SchemaObj | null {
  if (!s) return null;
  if (!s.allOf) return s;
  const merged: SchemaObj = { type: "object" };
  for (const part of s.allOf) {
    const flat = flattenSchema(part);
    if (!flat) continue;
    if (flat.properties) merged.properties = { ...merged.properties, ...flat.properties };
    if (flat.required) merged.required = [...(merged.required || []), ...flat.required];
    if (flat.description && !merged.description) merged.description = flat.description;
    if (flat.type) merged.type = flat.type;
  }
  return merged;
}

type SectionKey = "route" | "auth" | "summary" | "parameters" | "requestBody" | "responses";

const SECTION_ORDER: SectionKey[] = ["route", "auth", "summary", "parameters", "requestBody", "responses"];

const SECTION_LABELS: Record<SectionKey, string> = {
  route: "Route", auth: "Auth", summary: "Summary",
  parameters: "Parameters", requestBody: "Request Body", responses: "Responses",
};

function sectionChanged(key: SectionKey, before: Record<string, any>, after: Record<string, any>): boolean {
  switch (key) {
    case "route": return before.method !== after.method || before.path !== after.path;
    case "auth": return before.authType !== after.authType;
    case "summary": return before.summary !== after.summary;
    case "parameters": return !eq(before.parameters, after.parameters);
    case "requestBody": return !eq(before.requestBody, after.requestBody);
    case "responses": return !eq(before.responses, after.responses);
  }
}

function sectionPresent(key: SectionKey, spec: Record<string, any>): boolean {
  switch (key) {
    case "route": return !!(spec.method || spec.path);
    case "auth": return !!spec.authType;
    case "summary": return !!spec.summary;
    case "parameters": return Array.isArray(spec.parameters) && spec.parameters.length > 0;
    case "requestBody": return !!spec.requestBody;
    case "responses": return !!spec.responses && Object.keys(spec.responses).length > 0;
  }
}

/* ════════════════════════════════════════════════════════════════════
   SchemaTree — recursive collapsible tree for JSON schemas
   ════════════════════════════════════════════════════════════════════ */

function SchemaTree({ schema, depth = 0, diffSchema, side = "after" }: {
  schema: SchemaObj | null; depth?: number; diffSchema?: SchemaObj | null;
  side?: DiffSide;
}) {
  const flat = flattenSchema(schema);
  if (!flat) return <span className="text-[12px] text-slate-300 italic">—</span>;

  const props = flat.properties;
  const req = new Set(flat.required || []);

  // Array type
  if (schemaType(flat) === "array" && flat.items) {
    const itemsFlat = flattenSchema(flat.items);
    const isObjectArray = itemsFlat && (itemsFlat.properties || schemaType(itemsFlat) === "object");
    if (isObjectArray) {
      return (
        <div style={{ paddingLeft: depth > 0 ? 12 : 0 }}>
          <SchemaTree schema={flat.items} depth={depth + 1}
            diffSchema={diffSchema ? flattenSchema(diffSchema)?.items : undefined}
            side={side} />
        </div>
      );
    }
    // Primitive array — just show the type chip
    return (
      <span className="inline-flex items-center gap-1.5" style={{ paddingLeft: depth > 0 ? 12 : 0 }}>
        <TypeChip type={schemaTypeLabel({ type: "array", items: itemsFlat })} />
      </span>
    );
  }

  // Object with properties — walk the union of (this, other) keys so we
  // emit alignment placeholders for keys present only on the other side.
  const diffFlat = diffSchema ? flattenSchema(diffSchema) : null;
  const diffProps = diffFlat?.properties || {};
  const hasMyProps = props && Object.keys(props).length > 0;
  const hasOtherProps = Object.keys(diffProps).length > 0;
  if (hasMyProps || hasOtherProps) {
    const orderedKeys: string[] = [];
    if (props) for (const k of Object.keys(props)) orderedKeys.push(k);
    for (const k of Object.keys(diffProps)) {
      if (!props || !(k in props)) orderedKeys.push(k);
    }

    return (
      <div style={{ paddingLeft: depth > 0 ? 12 : 0 }} className="space-y-0.5">
        {orderedKeys.map(key => {
          const here = props ? props[key] : undefined;
          const there = diffProps[key];
          const isEqual = here !== undefined && there !== undefined && eq(here, there);
          const status = classifyItem(side, here !== undefined, there !== undefined, isEqual);
          const val = (here || there) as SchemaObj;
          // Only object/object[] are expandable — primitive arrays (string[], integer[]) are not
          const nested = val.properties || val.allOf
            || (schemaType(val) === "array" && val.items && (val.items.properties || val.items.allOf || schemaType(val.items) === "object"));
          return <SchemaProperty key={key} name={key} schema={val} required={here ? req.has(key) : false}
            depth={depth} status={status} hasNested={!!nested}
            diffSchema={status === "modified" ? there : undefined}
            side={side} />;
        })}
      </div>
    );
  }

  // Primitive
  return (
    <span className="inline-flex items-center gap-1.5">
      <TypeChip type={flat.enum ? "enum" : schemaTypeName(flat) || "any"} />
      {flat.format && <span className="text-[11px] text-slate-400">({flat.format})</span>}
      {flat.enum && <span className="text-[11px] text-slate-400">[{flat.enum.join(", ")}]</span>}
    </span>
  );
}

function SchemaProperty({ name, schema, required, depth, status, hasNested, diffSchema, side }: {
  name: string; schema: SchemaObj; required: boolean; depth: number;
  status: ItemStatus; hasNested: boolean; diffSchema?: SchemaObj; side: DiffSide;
}) {
  const ctx = useExpand();
  const key = `${depth}:${name}`;
  const defaultOpen = depth < 2;
  const open = ctx ? (ctx.expandMap[key] ?? defaultOpen) : defaultOpen;
  const toggleOpen = () => { if (ctx) ctx.toggle(key); };
  const isPlaceholder = status === "placeholder";
  const borderAccent =
    status === "added" ? "border-l-2 border-l-emerald-300"
    : status === "removed" ? "border-l-2 border-l-red-300"
    : status === "modified" ? "border-l-2 border-l-blue-300"
    : "";
  return (
    <div className={`rounded ${STATUS_BG[status]} ${borderAccent} ${isPlaceholder ? "opacity-50 italic" : ""}`}>
      <div className="flex items-center gap-1.5 py-1 px-1.5 cursor-pointer"
        onClick={() => hasNested && !isPlaceholder && toggleOpen()}>
        {hasNested && !isPlaceholder ? (
          open ? <ChevronDown className="size-3 text-slate-400 shrink-0" />
            : <ChevronRight className="size-3 text-slate-400 shrink-0" />
        ) : <span className="w-3 shrink-0" />}
        <span className="font-mono text-[12px] font-medium text-slate-700">{name}</span>
        <TypeChip type={schema.enum && schemaType(schema) !== "array" ? "enum" : schemaTypeLabel(schema)} />
        {required && <span className="text-[11px] text-red-400">req</span>}
        {schema.nullable && <span className="text-[11px] text-slate-400">null</span>}
        {isPlaceholder && (
          <span className="text-[10px] text-slate-300 ml-1">
            ({side === "before" ? "added on right" : "removed on left"})
          </span>
        )}
      </div>
      {schema.description && !isPlaceholder && <p className="text-[11px] text-slate-400 leading-snug ml-[22px] px-1.5 pb-1">{schema.description}</p>}
      {hasNested && open && !isPlaceholder && (
        <div className="ml-3 border-l border-slate-200 pl-1">
          <SchemaTree schema={schema} depth={depth + 1} diffSchema={diffSchema} side={side} />
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   Section renderers — one side at a time
   ════════════════════════════════════════════════════════════════════ */

export function RouteSection({ spec }: { spec: Record<string, any> }) {
  const method = (spec.method || "GET").toUpperCase();
  const cls = METHOD_COLORS[method] || "bg-slate-100 text-slate-600";
  return (
    <div className="flex items-center gap-2">
      <span className={`text-[12px] font-bold px-1.5 py-0.5 rounded ${cls}`}>{method}</span>
      <span className="font-mono text-[14px] text-slate-700 font-medium">{spec.path || "/"}</span>
    </div>
  );
}

export function AuthSection({ spec }: { spec: Record<string, any> }) {
  const label = AUTH_LABELS[spec.authType] || spec.authType || "None";
  const color = AUTH_BADGE_COLORS[spec.authType] || "bg-slate-100 text-slate-500";
  return (
    <div className="flex items-center gap-2 text-[12px]">
      <span className={`px-1.5 py-0.5 rounded font-medium ${color}`}>{label}</span>
    </div>
  );
}

export function SummarySection({ spec }: { spec: Record<string, any> }) {
  return (
    <div className="text-[13px] text-slate-600 bg-slate-50 rounded p-2 border border-slate-100 whitespace-pre-wrap">
      {spec.summary || "—"}
    </div>
  );
}

export function ParametersSection({ params, diffParams, side = "after" }: {
  params: ApiParam[]; diffParams?: ApiParam[]; side?: DiffSide;
}) {
  const myMap = new Map(params.map(p => [p.name, p]));
  const otherMap = diffParams ? new Map(diffParams.map(p => [p.name, p])) : null;

  // Walk the union of names so we can render placeholder rows for items
  // that only exist on the OTHER side, keeping the two columns aligned.
  // Preserve original order: my-side params first, then other-only ones.
  const orderedNames: string[] = [];
  for (const p of params) orderedNames.push(p.name);
  if (otherMap) {
    for (const name of otherMap.keys()) {
      if (!myMap.has(name)) orderedNames.push(name);
    }
  }

  if (orderedNames.length === 0) {
    return <span className="text-[12px] text-slate-300 italic">None</span>;
  }

  return (
    <div className="space-y-0.5">
      {orderedNames.map((name, i) => {
        const here = myMap.get(name);
        const there = otherMap?.get(name);
        const status = classifyItem(side, !!here, !!there, here && there ? eq(here, there) : false);
        const p = here || there!;
        const isPlaceholder = status === "placeholder";
        return (
          <div key={`${name}:${i}`}
            className={`flex items-center gap-2 py-1 px-2 rounded ${STATUS_BG[status]} ${isPlaceholder ? "opacity-50 italic" : ""}`}>
            <span className="font-mono text-[12px] font-medium text-slate-700">{p.name}</span>
            <span className="text-[11px] text-slate-400">{p.in}</span>
            <TypeChip type={schemaTypeName(p.schema) || "string"} />
            {p.required && <span className="text-[11px] text-red-400">required</span>}
            {isPlaceholder && (
              <span className="text-[10px] text-slate-300 ml-1">
                ({side === "before" ? "added on right" : "removed on left"})
              </span>
            )}
            {p.description && !isPlaceholder && (
              <span className="text-[11px] text-slate-400 truncate ml-auto max-w-[160px]">{p.description}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function RequestBodySection({ schema, diffSchema, side = "after" }: {
  schema: SchemaObj | null; diffSchema?: SchemaObj | null; side?: DiffSide;
}) {
  if (!schema && !diffSchema) {
    return <span className="text-[12px] text-slate-300 italic">None</span>;
  }
  if (!schema) {
    // Other side has a body but this side doesn't — render placeholder.
    return (
      <div className="text-[12px] text-slate-300 italic opacity-60">
        ({side === "before" ? "request body added on right" : "request body removed on left"})
      </div>
    );
  }
  return <SchemaTree schema={schema} diffSchema={diffSchema} side={side} />;
}

export function ResponsesSection({ responses, diffResponses, side = "after" }: {
  responses: Record<string, ResponseObj>; diffResponses?: Record<string, ResponseObj>;
  side?: DiffSide;
}) {
  // Walk union of status codes so we render placeholder rows for codes
  // that only exist on the OTHER side. Sort numerically.
  const allCodes = new Set<string>(Object.keys(responses));
  if (diffResponses) for (const c of Object.keys(diffResponses)) allCodes.add(c);
  const codes = Array.from(allCodes).sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
  if (codes.length === 0) return <span className="text-[12px] text-slate-300 italic">None</span>;
  return (
    <div className="space-y-1">
      {codes.map(code => {
        const resp = responses[code];
        const diffResp = diffResponses?.[code];
        const status = classifyItem(side, !!resp, !!diffResp,
          resp && diffResp ? eq(resp, diffResp) : false);
        const codeNum = parseInt(code, 10);
        const codeColor = codeNum < 300 ? "text-emerald-600" : codeNum < 400 ? "text-blue-600"
          : codeNum < 500 ? "text-amber-600" : "text-red-600";
        return (
          <ResponseEntry key={code} code={code} codeColor={codeColor}
            resp={resp || diffResp!}
            status={status}
            side={side}
            diffResp={status === "modified" ? diffResp : undefined} />
        );
      })}
    </div>
  );
}

function ResponseEntry({ code, codeColor, resp, status, side, diffResp }: {
  code: string; codeColor: string; resp: ResponseObj;
  status: ItemStatus; side: DiffSide; diffResp?: ResponseObj;
}) {
  const [open, setOpen] = useState(false);
  const hasSchema = !!resp.schema;
  const isPlaceholder = status === "placeholder";
  return (
    <div className={`rounded ${STATUS_ROW_BG[status]} ${isPlaceholder ? "opacity-50 italic" : ""}`}>
      <div className="flex items-center gap-2 py-1 px-2 cursor-pointer" onClick={() => hasSchema && !isPlaceholder && setOpen(o => !o)}>
        {hasSchema && !isPlaceholder ? (
          open ? <ChevronDown className="size-3 text-slate-400 shrink-0" />
            : <ChevronRight className="size-3 text-slate-400 shrink-0" />
        ) : <span className="w-3 shrink-0" />}
        <span className={`font-mono text-[12px] font-bold ${codeColor}`}>{code}</span>
        <span className="text-[12px] text-slate-600 truncate">{resp.description || "—"}</span>
        {hasSchema && <TypeChip type={schemaTypeName(resp.schema) || "object"} />}
        {isPlaceholder && (
          <span className="text-[10px] text-slate-300 ml-1">
            ({side === "before" ? "added on right" : "removed on left"})
          </span>
        )}
      </div>
      {hasSchema && open && !isPlaceholder && (
        <div className="px-2 pb-1">
          <SchemaTree schema={resp.schema!} diffSchema={diffResp?.schema} side={side} />
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   CollapsibleSection — wraps each section with header + toggle
   ════════════════════════════════════════════════════════════════════ */

function CollapsibleSection({ label, changed, open, onToggle, children }: {
  label: string; changed: boolean; open: boolean; onToggle: () => void; children: React.ReactNode;
}) {
  return (
    <div className={`border rounded-md ${changed ? "border-blue-200" : "border-slate-150"}`}>
      <button onClick={onToggle}
        className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-left ${changed ? "bg-blue-50/40" : "bg-slate-50/60"} rounded-t-md`}>
        {open ? <ChevronDown className="size-3 text-slate-400" /> : <ChevronRight className="size-3 text-slate-400" />}
        <span className="text-[12px] font-semibold text-slate-600 uppercase tracking-wider">{label}</span>
        {changed && <span className="size-1.5 rounded-full bg-blue-400 shrink-0" />}
      </button>
      {open && <div className="px-2.5 py-2">{children}</div>}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
   SpecColumn — renders one side (before or after) with all sections
   ════════════════════════════════════════════════════════════════════ */

function SpecColumn({ spec, otherSpec, sectionStates, collapseState, onToggle, side }: {
  spec: Record<string, any> | null;
  otherSpec: Record<string, any> | null;
  sectionStates: Record<SectionKey, { changed: boolean; present: boolean }>;
  collapseState: Record<SectionKey, boolean>;
  onToggle: (key: SectionKey) => void;
  side: DiffSide;
}) {
  if (!spec) {
    return <span className="text-slate-300 italic text-[13px] p-3">No spec</span>;
  }

  return (
    <div className="space-y-1.5 p-3">
      {SECTION_ORDER.map(key => {
        const { changed, present } = sectionStates[key];
        const otherPresent = otherSpec ? sectionPresent(key, otherSpec) : false;
        if (!present && !otherPresent) return null;
        return (
          <CollapsibleSection key={key} label={SECTION_LABELS[key]}
            changed={changed} open={collapseState[key]} onToggle={() => onToggle(key)}>
            {!present ? (
              <span className="text-[12px] text-slate-300 italic">
                ({side === "before" ? "added on right" : "removed on left"})
              </span>
            ) : (
              <SectionContent sectionKey={key} spec={spec} otherSpec={otherSpec} side={side} />
            )}
          </CollapsibleSection>
        );
      })}
    </div>
  );
}

function SectionContent({ sectionKey, spec, otherSpec, side }: {
  sectionKey: SectionKey; spec: Record<string, any>; otherSpec: Record<string, any> | null;
  side: DiffSide;
}) {
  switch (sectionKey) {
    case "route": return <RouteSection spec={spec} />;
    case "auth": return <AuthSection spec={spec} />;
    case "summary": return <SummarySection spec={spec} />;
    case "parameters":
      return <ParametersSection params={spec.parameters || []}
        diffParams={otherSpec?.parameters} side={side} />;
    case "requestBody":
      return <RequestBodySection schema={spec.requestBody}
        diffSchema={otherSpec?.requestBody} side={side} />;
    case "responses":
      return <ResponsesSection responses={spec.responses || {}}
        diffResponses={otherSpec?.responses} side={side} />;
  }
}

/* ════════════════════════════════════════════════════════════════════
   ApiSpecDiffView — main exported component
   ════════════════════════════════════════════════════════════════════ */

export default function ApiSpecDiffView({
  beforeSpec, afterSpec, beforeLabel = "Before", afterLabel = "After",
}: ApiSpecDiffViewProps) {
  const before = beforeSpec || {};
  const after = afterSpec || {};

  const sectionStates = useMemo(() => {
    const states = {} as Record<SectionKey, { changed: boolean; present: boolean }>;
    for (const key of SECTION_ORDER) {
      const bPresent = beforeSpec ? sectionPresent(key, before) : false;
      const aPresent = afterSpec ? sectionPresent(key, after) : false;
      const changed = beforeSpec && afterSpec ? sectionChanged(key, before, after) : true;
      states[key] = { changed, present: bPresent || aPresent };
    }
    return states;
  }, [beforeSpec, afterSpec, before, after]);

  const beforeSectionStates = useMemo(() => {
    const s = {} as Record<SectionKey, { changed: boolean; present: boolean }>;
    for (const key of SECTION_ORDER) {
      s[key] = { changed: sectionStates[key].changed, present: beforeSpec ? sectionPresent(key, before) : false };
    }
    return s;
  }, [sectionStates, beforeSpec, before]);

  const afterSectionStates = useMemo(() => {
    const s = {} as Record<SectionKey, { changed: boolean; present: boolean }>;
    for (const key of SECTION_ORDER) {
      s[key] = { changed: sectionStates[key].changed, present: afterSpec ? sectionPresent(key, after) : false };
    }
    return s;
  }, [sectionStates, afterSpec, after]);

  // Shared collapse state — synced between left and right columns
  const [collapseState, setCollapseState] = useState<Record<SectionKey, boolean>>(() => {
    const init = {} as Record<SectionKey, boolean>;
    for (const key of SECTION_ORDER) {
      const bPresent = beforeSpec ? sectionPresent(key, before) : false;
      const aPresent = afterSpec ? sectionPresent(key, after) : false;
      const changed = beforeSpec && afterSpec ? sectionChanged(key, before, after) : true;
      init[key] = changed || !afterSpec || (!bPresent && !aPresent ? false : true);
    }
    return init;
  });

  const toggleSection = useCallback((key: SectionKey) => {
    setCollapseState(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // Horizontal scroll sync
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);
  const syncing = useRef(false);

  useEffect(() => {
    const left = leftRef.current;
    const right = rightRef.current;
    if (!left || !right) return;

    function onLeftScroll() {
      if (syncing.current) return;
      syncing.current = true;
      rightRef.current!.scrollLeft = leftRef.current!.scrollLeft;
      syncing.current = false;
    }
    function onRightScroll() {
      if (syncing.current) return;
      syncing.current = true;
      leftRef.current!.scrollLeft = rightRef.current!.scrollLeft;
      syncing.current = false;
    }

    left.addEventListener("scroll", onLeftScroll);
    right.addEventListener("scroll", onRightScroll);
    return () => {
      left.removeEventListener("scroll", onLeftScroll);
      right.removeEventListener("scroll", onRightScroll);
    };
  }, []);

  // Shared expand state for synced collapse/expand of schema trees across panels
  const [expandMap, setExpandMap] = useState<Record<string, boolean>>({});
  const toggleExpand = useCallback((key: string) => {
    setExpandMap(prev => ({ ...prev, [key]: !(prev[key] ?? (parseInt(key.split(":")[0]) < 2)) }));
  }, []);

  const changedCount = SECTION_ORDER.filter(k => sectionStates[k].changed && sectionStates[k].present).length;
  if (changedCount === 0 && beforeSpec && afterSpec) return null;

  return (
    <ExpandContext.Provider value={{ expandMap, toggle: toggleExpand }}>
      <div className="border-b border-slate-200 shrink-0">
        <div className="px-4 py-1.5 bg-violet-50/50 border-b border-slate-100 flex items-center gap-2">
          <span className="text-[12px] font-semibold text-violet-700 uppercase tracking-wider">Spec Changes</span>
          <span className="text-[12px] text-violet-500">
            {changedCount} section{changedCount !== 1 ? "s" : ""}
          </span>
        </div>
        <div className="flex max-h-[400px] overflow-hidden">
          <div ref={leftRef} className="flex-1 min-w-0 overflow-auto border-r border-slate-200">
            <div className="min-w-[70vw]">
              <div className="px-3 py-1 bg-red-50/30 border-b border-slate-100 sticky top-0 z-10">
                <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{beforeLabel}</span>
              </div>
              <SpecColumn spec={beforeSpec} otherSpec={afterSpec} sectionStates={beforeSectionStates}
                collapseState={collapseState} onToggle={toggleSection} side="before" />
            </div>
          </div>
          <div ref={rightRef} className="flex-1 min-w-0 overflow-auto">
            <div className="min-w-[70vw]">
              <div className="px-3 py-1 bg-emerald-50/30 border-b border-slate-100 sticky top-0 z-10">
                <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{afterLabel}</span>
              </div>
              <SpecColumn spec={afterSpec} otherSpec={beforeSpec} sectionStates={afterSectionStates}
                collapseState={collapseState} onToggle={toggleSection} side="after" />
            </div>
          </div>
        </div>
      </div>
    </ExpandContext.Provider>
  );
}
